import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useReducer, useState } from 'react';

import { GET_SETTINGS_USER_GROUPS, type SettingsGroup } from '../../../queries/skill-administration';

const EMPTY_GROUPS: readonly SettingsGroup[] = [];

/** Identity-scoped discovery shared by Settings navigation and the skill group picker. */
export function useUserNovaGroups(userUuid: string, requested = true) {
  const client = useApolloClient();
  const enabled = requested && !!userUuid;
  const [attempt, retry] = useReducer((value: number) => value + 1, 0);
  // Fresh data and retained page selection belong to this identity/open session.
  const session = useMemo(() => ({ enabled, userUuid }), [enabled, userUuid]);
  const request = useMemo(() => ({ session, attempt }), [session, attempt]);
  const [snapshot, setSnapshot] = useState<{
    request: object;
    status: 'ready' | 'error';
    groups: readonly SettingsGroup[];
  }>();

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const subscription = client
      .watchQuery({
        query: GET_SETTINGS_USER_GROUPS,
        // Authorization is implicit in the session, not a GraphQL variable. Do not cache it across users.
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
        context: { queryDeduplication: false },
      })
      .subscribe({
        next: ({ data, loading }) => {
          if (!active || loading) return;
          if (!Array.isArray(data?.getUserNovaGroups)) {
            setSnapshot({ request, status: 'error', groups: EMPTY_GROUPS });
            return;
          }
          setSnapshot({ request, status: 'ready', groups: data.getUserNovaGroups });
        },
        error: () => {
          if (active) setSnapshot({ request, status: 'error', groups: EMPTY_GROUPS });
        },
      });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [client, enabled, request]);

  const current = enabled && snapshot?.request === request ? snapshot : undefined;
  return { groups: current?.groups ?? EMPTY_GROUPS, status: current?.status ?? 'loading', session, retry } as const;
}
